import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, Calendar, Video, Megaphone, HeartHandshake, Mail, Users, Plus, Trash2, Flame, CalendarHeart, GraduationCap, KeyRound, Eye, EyeOff, ShieldCheck, Pencil, Check as CheckIcon, X, Image as ImageIcon, Upload, Link as LinkIcon, FileEdit, RotateCcw, ArrowUp, ArrowDown, Crop, ZoomIn, Sparkles, Star, Download } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const TABS = [
  { id: "overview", label: "Overview", icon: Users },
  { id: "site-content", label: "Site Content", icon: FileEdit },
  { id: "visitors", label: "First-Time Visitors", icon: CalendarHeart },
  { id: "sunday-school", label: "Sunday School", icon: GraduationCap },
  { id: "events", label: "Events", icon: Calendar },
  { id: "sermons", label: "Sermons", icon: Video },
  { id: "ministries", label: "Ministries", icon: Flame },
  { id: "gallery", label: "Gallery", icon: ImageIcon },
  { id: "testimonies", label: "Testimonies", icon: Sparkles },
  { id: "announcements", label: "Announcements", icon: Megaphone },
  { id: "prayer", label: "Prayer Requests", icon: HeartHandshake },
  { id: "newsletter", label: "Newsletter", icon: Mail },
  { id: "contact", label: "Messages", icon: Mail },
];

export default function Admin() {
  const { user, logout, updateToken } = useAuth();
  const [tab, setTab] = useState("overview");
  const [pwOpen, setPwOpen] = useState(false);

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 bg-[#080808] sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Flame className="w-5 h-5 text-[#ff5c00]" />
            <span className="font-display text-xl uppercase tracking-tight">UGYC <span className="text-[#ff5c00]">Admin</span></span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline font-mono-accent text-xs text-zinc-500 uppercase tracking-widest">{user?.email}</span>
            <button
              data-testid="admin-open-change-password"
              onClick={() => setPwOpen(true)}
              className="inline-flex items-center gap-2 border border-white/10 hover:border-[#ff5c00] hover:text-[#ff5c00] font-mono-accent text-xs uppercase tracking-widest px-4 py-2 rounded-sm"
            >
              <KeyRound className="w-3 h-3" /> Change Password
            </button>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="inline-flex items-center gap-2 border border-white/10 hover:border-[#ff5c00] hover:text-[#ff5c00] font-mono-accent text-xs uppercase tracking-widest px-4 py-2 rounded-sm"
            >
              <LogOut className="w-3 h-3" /> Logout
            </button>
          </div>
        </div>
      </header>

      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} onTokenRotated={updateToken} />

      <div className="max-w-[1400px] mx-auto px-6 py-8 grid lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-3">
          <nav className="bg-[#0f0f14] border border-white/10 rounded-sm p-2 sticky top-24">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm font-mono-accent text-xs uppercase tracking-widest transition-colors ${
                  tab === t.id
                    ? "bg-[#ff5c00]/10 text-white border border-[#ff5c00]/40"
                    : "text-zinc-400 hover:text-white border border-transparent"
                }`}
              >
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </nav>
        </aside>
        <main className="lg:col-span-9 space-y-6">
          {tab === "overview" && <Overview />}
          {tab === "site-content" && <SiteContentManager />}
          {tab === "visitors" && <VisitorList />}
          {tab === "sunday-school" && <SundaySchoolAdmin />}
          {tab === "events" && <EventsManager />}
          {tab === "sermons" && <SermonsManager />}
          {tab === "ministries" && <MinistriesManager />}
          {tab === "gallery" && <GalleryManager />}
          {tab === "testimonies" && <TestimoniesManager />}
          {tab === "announcements" && <AnnouncementsManager />}
          {tab === "prayer" && <PrayerList />}
          {tab === "newsletter" && <NewsletterList />}
          {tab === "contact" && <ContactList />}
        </main>
      </div>
    </div>
  );
}

function ChangePasswordDialog({ open, onOpenChange, onTokenRotated }) {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [show, setShow] = useState({ cur: false, next: false, confirm: false });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const reset = () => {
    setForm({ current_password: "", new_password: "", confirm_password: "" });
    setShow({ cur: false, next: false, confirm: false });
    setStatus("idle");
    setError("");
  };

  const close = (next) => {
    onOpenChange(next);
    if (!next) reset();
  };

  // password strength meter
  const np = form.new_password;
  const checks = {
    length: np.length >= 8,
    letter: /[a-zA-Z]/.test(np),
    digit: /\d/.test(np),
    distinct: !!np && np !== form.current_password,
    matches: !!np && np === form.confirm_password,
  };
  const score = Object.values(checks).filter(Boolean).length;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!checks.length) return setError("New password must be at least 8 characters.");
    if (!checks.letter || !checks.digit) return setError("New password must include both letters and numbers.");
    if (!checks.distinct) return setError("New password must be different from the current one.");
    if (!checks.matches) return setError("New password and confirmation do not match.");

    setStatus("loading");
    try {
      const res = await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      if (res.data?.token && onTokenRotated) onTokenRotated(res.data.token);
      setStatus("success");
      setTimeout(() => close(false), 1400);
    } catch (err) {
      setStatus("error");
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not update password.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="change-password-dialog" className="bg-[#0a0a0d] border border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase tracking-tight flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-[#ff5c00]" /> Change Password
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm">
            Use at least 8 characters with letters and numbers. You'll stay signed in on this device.
          </DialogDescription>
        </DialogHeader>

        {status === "success" ? (
          <div data-testid="change-password-success" className="py-8 text-center space-y-3">
            <div className="w-14 h-14 mx-auto rounded-full bg-[#22c55e]/15 flex items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-[#22c55e]" />
            </div>
            <p className="font-display text-xl uppercase tracking-tight">Password updated</p>
            <p className="text-zinc-400 text-sm">Use the new password next time you sign in.</p>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="change-password-form" className="space-y-4">
            <PwField
              label="Current Password"
              value={form.current_password}
              onChange={(v) => setForm({ ...form, current_password: v })}
              show={show.cur}
              onToggle={() => setShow({ ...show, cur: !show.cur })}
              testId="cp-current"
              autoFocus
            />
            <PwField
              label="New Password"
              value={form.new_password}
              onChange={(v) => setForm({ ...form, new_password: v })}
              show={show.next}
              onToggle={() => setShow({ ...show, next: !show.next })}
              testId="cp-new"
            />
            <PwField
              label="Confirm New Password"
              value={form.confirm_password}
              onChange={(v) => setForm({ ...form, confirm_password: v })}
              show={show.confirm}
              onToggle={() => setShow({ ...show, confirm: !show.confirm })}
              testId="cp-confirm"
            />

            {/* Strength meter */}
            <div data-testid="cp-strength" className="space-y-2">
              <div className="flex gap-1.5">
                {[1, 2, 3, 4, 5].map((i) => (
                  <span
                    key={i}
                    className={`h-1 flex-1 rounded-full transition-colors ${
                      i <= score
                        ? score >= 4
                          ? "bg-[#22c55e]"
                          : score >= 2
                          ? "bg-[#ff5c00]"
                          : "bg-red-500"
                        : "bg-white/10"
                    }`}
                  />
                ))}
              </div>
              <ul className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] font-mono-accent uppercase tracking-widest">
                <Check label="8+ characters" ok={checks.length} />
                <Check label="A letter" ok={checks.letter} />
                <Check label="A number" ok={checks.digit} />
                <Check label="Different" ok={checks.distinct} />
                <Check label="Matches" ok={checks.matches} />
              </ul>
            </div>

            {error && <p data-testid="cp-error" className="text-red-400 text-sm">{error}</p>}

            <DialogFooter className="pt-2 gap-2 sm:gap-2">
              <button
                type="button"
                onClick={() => close(false)}
                data-testid="cp-cancel"
                className="font-mono-accent text-xs uppercase tracking-widest px-4 py-2.5 rounded-sm border border-white/10 hover:border-white/30 text-zinc-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="cp-submit"
                className="inline-flex items-center gap-2 bg-[#ff5c00] hover:bg-[#ff7a33] disabled:opacity-60 text-white font-mono-accent text-xs uppercase tracking-widest px-5 py-2.5 rounded-sm transition-colors"
              >
                {status === "loading" ? "Updating…" : "Update Password"}
              </button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PwField({ label, value, onChange, show, onToggle, testId, autoFocus }) {
  return (
    <label className="block">
      <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2 block">{label}</span>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          required
          autoFocus={autoFocus}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          className="w-full bg-[#050505] border border-white/10 px-4 py-3 pr-11 text-sm text-white focus:outline-none focus:border-[#ff5c00] rounded-sm"
        />
        <button
          type="button"
          onClick={onToggle}
          data-testid={`${testId}-toggle`}
          tabIndex={-1}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-[#ff5c00] p-1"
          aria-label={show ? "Hide password" : "Show password"}
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </label>
  );
}

function Check({ label, ok }) {
  return (
    <li className={`flex items-center gap-1.5 ${ok ? "text-[#22c55e]" : "text-zinc-500"}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${ok ? "bg-[#22c55e]" : "bg-zinc-700"}`} /> {label}
    </li>
  );
}

function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
  }, []);
  const cards = [
    { label: "First-Time Visitors", v: stats?.visitors, color: "text-[#ff5c00]" },
    { label: "Uncontacted Visitors", v: stats?.visitors_uncontacted, color: "text-red-400" },
    { label: "SS Students", v: stats?.ss_students, color: "text-[#ff5c00]" },
    { label: "Marked Today", v: stats?.ss_attendance_today, color: "text-[#22c55e]" },
    { label: "Events", v: stats?.events, color: "text-[#ff5c00]" },
    { label: "Sermons", v: stats?.sermons, color: "text-[#ff5c00]" },
    { label: "Prayer Requests", v: stats?.prayer_requests, color: "text-[#ffe600]" },
    { label: "Unhandled Prayers", v: stats?.prayer_requests_unhandled, color: "text-red-400" },
    { label: "Subscribers", v: stats?.newsletter_subscribers, color: "text-[#22c55e]" },
    { label: "Messages", v: stats?.contact_messages, color: "text-[#22c55e]" },
    { label: "Gallery", v: stats?.gallery_images, color: "text-[#ff5c00]" },
  ];
  return (
    <div data-testid="admin-overview">
      <h2 className="font-display text-3xl uppercase tracking-tight mb-6">Overview</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-[#0f0f14] border border-white/10 p-6 rounded-sm">
            <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-500">{c.label}</p>
            <p className={`font-display text-5xl mt-2 ${c.color}`}>{c.v ?? "—"}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Section({ title, children, actions }) {
  return (
    <section>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-3xl uppercase tracking-tight">{title}</h2>
        {actions}
      </div>
      {children}
    </section>
  );
}

function Input({ label, ...rest }) {
  return (
    <label className="block">
      <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-1.5 block">{label}</span>
      <input className="w-full bg-[#0a0a0d] border border-white/10 px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#ff5c00] rounded-sm" {...rest} />
    </label>
  );
}

function Textarea({ label, ...rest }) {
  return (
    <label className="block">
      <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-1.5 block">{label}</span>
      <textarea rows={3} className="w-full bg-[#0a0a0d] border border-white/10 px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#ff5c00] rounded-sm resize-none" {...rest} />
    </label>
  );
}

function EventsManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", date: "", time: "", location: "RCCG UGYC Hall, Dubai", image_url: "", category: "Service" });
  const [error, setError] = useState("");

  const load = () => api.get("/events").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/admin/events", form);
      setForm({ title: "", description: "", date: "", time: "", location: "RCCG UGYC Hall, Dubai", image_url: "", category: "Service" });
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/events/${id}`);
    load();
  };

  return (
    <Section title="Events">
      <form onSubmit={add} data-testid="event-form" className="bg-[#0f0f14] border border-white/10 rounded-sm p-6 mb-6 grid sm:grid-cols-2 gap-4">
        <Input label="Title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="event-title" />
        <Input label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="event-category" />
        <Input label="Date" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="event-date" />
        <Input label="Time" required value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="event-time" placeholder="19:00" />
        <Input label="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="event-location" />
        <Input label="Image URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="event-image" />
        <div className="sm:col-span-2">
          <Textarea label="Description" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="event-description" />
        </div>
        {error && <p className="text-red-400 text-sm sm:col-span-2">{error}</p>}
        <div className="sm:col-span-2">
          <button data-testid="event-create" className="inline-flex items-center gap-2 bg-[#ff5c00] hover:bg-[#ff7a33] text-white font-mono-accent text-xs uppercase tracking-widest px-5 py-3 rounded-sm">
            <Plus className="w-4 h-4" /> Create Event
          </button>
        </div>
      </form>

      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={it.id} data-testid={`event-row-${i}`} className="flex items-center justify-between bg-[#0f0f14] border border-white/10 rounded-sm p-4">
            <div>
              <p className="font-display text-lg uppercase tracking-tight">{it.title}</p>
              <p className="text-xs text-zinc-500 font-mono-accent uppercase tracking-widest">{it.date} · {it.time} · {it.category}</p>
            </div>
            <button onClick={() => remove(it.id)} data-testid={`event-delete-${i}`} className="text-zinc-500 hover:text-red-400 p-2"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </Section>
  );
}

function SermonsManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    title: "", speaker: "", date: "",
    sermon_type: "video",
    youtube_url: "",
    document_url: "",
    text_content: "",
    description: "",
  });
  const [error, setError] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");

  const load = () => api.get("/sermons").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = { ...form };
      if (form.sermon_type === "video") {
        if (!payload.youtube_url) {
          setError("YouTube URL is required for a video sermon.");
          return;
        }
        delete payload.document_url;
        delete payload.text_content;
      } else {
        if (!payload.document_url && !payload.text_content) {
          setError("Add a document URL or paste the sermon notes.");
          return;
        }
        delete payload.youtube_url;
      }
      await api.post("/admin/sermons", payload);
      setForm({ title: "", speaker: "", date: "", sermon_type: form.sermon_type, youtube_url: "", document_url: "", text_content: "", description: "" });
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => { await api.delete(`/admin/sermons/${id}`); load(); };

  const refreshYouTube = async () => {
    setSyncing(true);
    setSyncMsg("");
    try {
      const r = await api.post("/youtube/refresh");
      setSyncMsg(`Synced ${r.data.count} videos from YouTube.`);
    } catch (e) {
      setSyncMsg("Sync failed. Try again.");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Section
      title="Sermons"
      actions={
        <div className="flex items-center gap-2">
          {syncMsg && <span className="text-xs text-zinc-400">{syncMsg}</span>}
          <button
            onClick={refreshYouTube}
            disabled={syncing}
            data-testid="sermon-yt-sync"
            className="inline-flex items-center gap-2 border border-white/10 hover:border-[#ff5c00] hover:text-[#ff5c00] font-mono-accent text-[11px] uppercase tracking-widest px-3 py-2 rounded-sm disabled:opacity-50"
          >
            {syncing ? "Syncing…" : "Refresh YouTube"}
          </button>
        </div>
      }
    >
      <form onSubmit={add} data-testid="sermon-form" className="bg-[#0f0f14] border border-white/10 rounded-sm p-6 mb-6 space-y-4">
        <div className="flex gap-2">
          {[
            { id: "video", label: "Video (YouTube)" },
            { id: "document", label: "Document / Notes" },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setForm({ ...form, sermon_type: t.id })}
              data-testid={`sermon-type-${t.id}`}
              className={`font-mono-accent text-[11px] tracking-[0.2em] uppercase px-3 py-2 rounded-sm border ${
                form.sermon_type === t.id
                  ? "border-[#ff5c00] text-white bg-[#ff5c00]/10"
                  : "border-white/10 text-zinc-400 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Input label="Title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="sermon-title" />
          <Input label="Speaker" required value={form.speaker} onChange={(e) => setForm({ ...form, speaker: e.target.value })} data-testid="sermon-speaker" />
          <Input label="Date" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="sermon-date" />

          {form.sermon_type === "video" ? (
            <Input
              label="YouTube URL"
              required
              value={form.youtube_url}
              onChange={(e) => setForm({ ...form, youtube_url: e.target.value })}
              data-testid="sermon-youtube"
              placeholder="https://www.youtube.com/watch?v=…"
            />
          ) : (
            <Input
              label="Document URL (optional)"
              value={form.document_url}
              onChange={(e) => setForm({ ...form, document_url: e.target.value })}
              data-testid="sermon-doc-url"
              placeholder="Google Drive / Dropbox / PDF link"
            />
          )}
        </div>

        {form.sermon_type === "document" && (
          <div>
            <Textarea
              label="Sermon Notes / Text Content"
              rows={6}
              value={form.text_content}
              onChange={(e) => setForm({ ...form, text_content: e.target.value })}
              data-testid="sermon-text-content"
              placeholder="Paste the full sermon notes here — supports plain text and line breaks."
            />
            <p className="text-[10px] text-zinc-500 mt-1 font-mono-accent uppercase tracking-widest">
              Tip: include scripture references, key points, and prayer prompts.
            </p>
          </div>
        )}

        <Textarea label="Short Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="sermon-description" />

        {error && <p className="text-red-400 text-sm">{error}</p>}
        <div>
          <button data-testid="sermon-create" className="inline-flex items-center gap-2 bg-[#ff5c00] hover:bg-[#ff7a33] text-white font-mono-accent text-xs uppercase tracking-widest px-5 py-3 rounded-sm">
            <Plus className="w-4 h-4" /> Add Sermon
          </button>
        </div>
      </form>

      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={it.id} data-testid={`sermon-row-${i}`} className="flex items-center justify-between bg-[#0f0f14] border border-white/10 rounded-sm p-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-display text-lg uppercase tracking-tight">{it.title}</p>
                <span className={`text-[10px] font-mono-accent uppercase tracking-widest px-2 py-0.5 rounded-sm ${
                  (it.sermon_type || "video") === "document"
                    ? "bg-[#ffe600]/15 text-[#ffe600]"
                    : "bg-red-600/20 text-red-300"
                }`}>
                  {(it.sermon_type || "video") === "document" ? "Document" : "Video"}
                </span>
              </div>
              <p className="text-xs text-zinc-500 font-mono-accent uppercase tracking-widest mt-1">{it.date} · {it.speaker}</p>
            </div>
            <button onClick={() => remove(it.id)} data-testid={`sermon-delete-${i}`} className="text-zinc-500 hover:text-red-400 p-2 shrink-0"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {items.length === 0 && <p className="text-zinc-500 text-sm py-6 text-center">No manually added sermons yet — videos auto-stream from YouTube on the public page.</p>}
      </div>
    </Section>
  );
}

function AnnouncementsManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", is_pinned: false });
  const load = () => api.get("/announcements").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    await api.post("/admin/announcements", form);
    setForm({ title: "", body: "", is_pinned: false });
    load();
  };
  const remove = async (id) => { await api.delete(`/admin/announcements/${id}`); load(); };
  return (
    <Section title="Announcements">
      <form onSubmit={add} data-testid="announcement-form" className="bg-[#0f0f14] border border-white/10 rounded-sm p-6 mb-6 space-y-4">
        <Input label="Title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="ann-title" />
        <Textarea label="Body" required value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="ann-body" />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.is_pinned} onChange={(e) => setForm({ ...form, is_pinned: e.target.checked })} data-testid="ann-pinned" className="accent-[#ff5c00]" /> Pinned
        </label>
        <button data-testid="ann-create" className="inline-flex items-center gap-2 bg-[#ff5c00] hover:bg-[#ff7a33] text-white font-mono-accent text-xs uppercase tracking-widest px-5 py-3 rounded-sm">
          <Plus className="w-4 h-4" /> Post Announcement
        </button>
      </form>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={it.id} data-testid={`ann-row-${i}`} className="flex items-start justify-between bg-[#0f0f14] border border-white/10 rounded-sm p-4">
            <div>
              <p className="font-display text-lg uppercase tracking-tight">{it.title} {it.is_pinned && <span className="text-[10px] ml-2 px-2 py-0.5 bg-[#ff5c00]/20 text-[#ff5c00] rounded-sm font-mono-accent">PINNED</span>}</p>
              <p className="text-sm text-zinc-400 mt-1">{it.body}</p>
            </div>
            <button onClick={() => remove(it.id)} data-testid={`ann-delete-${i}`} className="text-zinc-500 hover:text-red-400 p-2"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </Section>
  );
}

function PrayerList() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/prayer-requests").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const mark = async (id) => { await api.put(`/admin/prayer-requests/${id}/handled`); load(); };
  return (
    <Section title="Prayer Requests">
      <div className="space-y-3">
        {items.length === 0 && <p className="text-zinc-500">No prayer requests yet.</p>}
        {items.map((p, i) => (
          <div key={p.id} data-testid={`prayer-row-${i}`} className={`bg-[#0f0f14] border rounded-sm p-5 ${p.handled ? "border-white/5 opacity-60" : "border-white/10"}`}>
            <div className="flex items-center justify-between mb-2">
              <p className="font-display uppercase tracking-tight">{p.is_anonymous ? "Anonymous" : p.name}</p>
              <p className="text-[10px] font-mono-accent uppercase tracking-widest text-zinc-500">{p.created_at?.slice(0, 10)}</p>
            </div>
            <p className="text-zinc-300 text-sm whitespace-pre-wrap">{p.request}</p>
            <div className="flex items-center gap-3 mt-3 text-xs text-zinc-500">
              {p.email && <span>{p.email}</span>}
              {p.phone && <span>{p.phone}</span>}
              {!p.handled && (
                <button data-testid={`prayer-mark-handled-${i}`} onClick={() => mark(p.id)} className="ml-auto bg-[#22c55e]/10 hover:bg-[#22c55e]/20 text-[#22c55e] px-3 py-1 rounded-sm font-mono-accent uppercase tracking-widest">Mark Handled</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function FellowshipsEditor({ data, onSaved }) {
  const [form, setForm] = useState({ eyebrow: "", heading: "", intro: "", items: [] });
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");

  useEffect(() => {
    setForm({
      eyebrow: data.eyebrow ?? "",
      heading: data.heading ?? "",
      intro: data.intro ?? "",
      items: Array.isArray(data.items) ? data.items.map((s) => ({ ...s })) : [],
    });
  }, [data]);

  const setItem = (i, patch) => {
    const items = [...form.items]; items[i] = { ...items[i], ...patch }; setForm({ ...form, items });
  };
  const addItem = () => setForm({ ...form, items: [...form.items, { name: "", location: "", day: "", time: "", address: "", coordinator: "", phone: "", is_online: false }] });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const move = (i, dir) => {
    const next = i + dir; if (next < 0 || next >= form.items.length) return;
    const items = [...form.items]; [items[i], items[next]] = [items[next], items[i]]; setForm({ ...form, items });
  };

  const save = async (e) => {
    e.preventDefault(); setSaving(true); setStatus("");
    try { await api.put(`/admin/content/home_fellowships`, { data: form }); setStatus("Saved ✓"); onSaved(); setTimeout(() => setStatus(""), 1800); }
    catch { setStatus("Save failed"); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    if (!window.confirm("Reset house fellowship centres to defaults?")) return;
    try { await api.post(`/admin/content/home_fellowships/reset`); onSaved(); setStatus("Reset"); setTimeout(() => setStatus(""), 1800); } catch (e) { /* noop */ }
  };

  return (
    <form onSubmit={save} data-testid="content-block-home_fellowships" className="bg-[#0f0f14] border border-white/10 rounded-sm p-6">
      <div className="flex items-start justify-between mb-5">
        <div>
          <h3 className="font-display text-xl uppercase tracking-tight">Home — House Fellowship Centres</h3>
          <p className="text-xs text-zinc-500 mt-1">Fellowship cells across Dubai (and online). Add coordinator contact for each.</p>
        </div>
        <button type="button" onClick={reset} data-testid="content-reset-home_fellowships" className="inline-flex items-center gap-1.5 text-zinc-500 hover:text-[#ff5c00] font-mono-accent text-[10px] uppercase tracking-widest">
          <RotateCcw className="w-3 h-3" /> Reset
        </button>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <Input label="Eyebrow" value={form.eyebrow} onChange={(e) => setForm({ ...form, eyebrow: e.target.value })} data-testid="content-fellowships-eyebrow" />
        <Input label="Heading" value={form.heading} onChange={(e) => setForm({ ...form, heading: e.target.value })} data-testid="content-fellowships-heading" />
      </div>
      <Textarea label="Intro" value={form.intro} onChange={(e) => setForm({ ...form, intro: e.target.value })} rows={2} data-testid="content-fellowships-intro" />

      <div className="space-y-3 mt-5">
        {form.items.map((it, i) => (
          <div key={i} data-testid={`fellowship-row-${i}`} className={`bg-[#0a0a0d] border rounded-sm p-4 ${it.is_online ? "border-[#ff5c00]/30" : "border-white/10"}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="font-mono-accent text-[10px] uppercase tracking-widest text-zinc-500">Centre #{i + 1}</span>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`fellowship-up-${i}`} className="text-zinc-400 hover:text-[#ff5c00] disabled:opacity-30 p-1.5"><ArrowUp className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === form.items.length - 1} data-testid={`fellowship-down-${i}`} className="text-zinc-400 hover:text-[#ff5c00] disabled:opacity-30 p-1.5"><ArrowDown className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => removeItem(i)} data-testid={`fellowship-remove-${i}`} className="text-zinc-500 hover:text-red-400 p-1.5"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Input label="Centre Name" value={it.name || ""} onChange={(e) => setItem(i, { name: e.target.value })} data-testid={`fellowship-name-${i}`} />
              <Input label="Location / Area" value={it.location || ""} onChange={(e) => setItem(i, { location: e.target.value })} data-testid={`fellowship-location-${i}`} />
              <Input label="Day" value={it.day || ""} onChange={(e) => setItem(i, { day: e.target.value })} data-testid={`fellowship-day-${i}`} />
              <Input label="Time" value={it.time || ""} onChange={(e) => setItem(i, { time: e.target.value })} data-testid={`fellowship-time-${i}`} />
              <Input label="Address" value={it.address || ""} onChange={(e) => setItem(i, { address: e.target.value })} data-testid={`fellowship-address-${i}`} />
              <Input label="Coordinator Name" value={it.coordinator || ""} onChange={(e) => setItem(i, { coordinator: e.target.value })} data-testid={`fellowship-coord-${i}`} />
              <Input label="Coordinator Phone" value={it.phone || ""} onChange={(e) => setItem(i, { phone: e.target.value })} data-testid={`fellowship-phone-${i}`} />
              <label className="flex items-center gap-2 mt-6">
                <input type="checkbox" checked={!!it.is_online} onChange={(e) => setItem(i, { is_online: e.target.checked })} data-testid={`fellowship-online-${i}`} className="w-4 h-4 accent-[#ff5c00]" />
                <span className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-zinc-300">Online Centre</span>
              </label>
            </div>
          </div>
        ))}
      </div>
      <button type="button" onClick={addItem} data-testid="fellowship-add" className="mt-4 inline-flex items-center gap-1 text-[#ff5c00] hover:text-white font-mono-accent text-[10px] uppercase tracking-widest">
        <Plus className="w-3 h-3" /> Add Centre
      </button>

      <div className="flex items-center gap-3 mt-5 pt-4 border-t border-white/5">
        <button type="submit" disabled={saving} data-testid="content-save-home_fellowships" className="inline-flex items-center gap-2 bg-[#ff5c00] hover:bg-[#ff7a33] disabled:opacity-60 text-white font-mono-accent text-xs uppercase tracking-widest px-5 py-2.5 rounded-sm">
          <CheckIcon className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save Changes"}
        </button>
        {status && <span className="text-[11px] font-mono-accent uppercase tracking-widest text-[#22c55e]">{status}</span>}
      </div>
    </form>
  );
}


function NewsletterList() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/newsletter").then((r) => setItems(r.data)); }, []);
  return (
    <Section title="Newsletter Subscribers">
      <div className="bg-[#0f0f14] border border-white/10 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#080808] text-zinc-500 font-mono-accent text-[10px] uppercase tracking-widest">
            <tr><th className="text-left p-3">Email</th><th className="text-left p-3">Name</th><th className="text-left p-3">Subscribed</th></tr>
          </thead>
          <tbody>
            {items.map((s, i) => (
              <tr key={s.id} data-testid={`newsletter-row-${i}`} className="border-t border-white/5">
                <td className="p-3 text-white">{s.email}</td>
                <td className="p-3 text-zinc-400">{s.name || "—"}</td>
                <td className="p-3 text-zinc-500 font-mono-accent text-xs">{s.created_at?.slice(0, 10)}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={3} className="p-6 text-center text-zinc-500">No subscribers yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function ShareModeBadge({ mode }) {
  const map = {
    private: { label: "✗ Private", cls: "text-zinc-500" },
    named: { label: "✓ Share with name", cls: "text-[#22c55e]" },
    anonymous: { label: "✓ Share anonymously", cls: "text-[#ff5c00]" },
  };
  const m = map[mode] || map.private;
  return <span className={m.cls}>{m.label}</span>;
}


function TestimoniesManager() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all"); // all | pending | approved | featured
  const load = () => api.get("/admin/testimonies").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const update = async (id, patch) => {
    await api.put(`/admin/testimonies/${id}`, patch);
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this testimony permanently? This cannot be undone.")) return;
    await api.delete(`/admin/testimonies/${id}`);
    load();
  };

  const downloadCard = async (id) => {
    try {
      const r = await api.get(`/admin/testimonies/${id}/card.jpg`, { responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `testimony-${id}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      window.alert("Could not generate the share card. Make sure the testimony is approved and not private.");
    }
  };

  const filtered = items.filter((t) => {
    if (filter === "pending") return !t.approved;
    if (filter === "approved") return t.approved;
    if (filter === "featured") return t.featured;
    return true;
  });

  const counts = {
    all: items.length,
    pending: items.filter((t) => !t.approved).length,
    approved: items.filter((t) => t.approved).length,
    featured: items.filter((t) => t.featured).length,
  };

  return (
    <Section title="Testimonies">
      <p className="text-zinc-400 text-sm mb-5 max-w-2xl">
        Review submissions, approve to display publicly (only if the submitter consented), and feature the strongest stories.
      </p>

      <div className="flex flex-wrap gap-2 mb-5">
        {["all", "pending", "approved", "featured"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`testimony-filter-${f}`}
            className={`font-mono-accent text-[10px] tracking-[0.18em] uppercase px-3 py-1.5 rounded-sm border transition-colors ${
              filter === f
                ? "bg-[#ff5c00]/10 text-white border-[#ff5c00]/40"
                : "text-zinc-400 border-white/10 hover:text-white hover:border-white/30"
            }`}
          >
            {f} <span className="ml-1 text-zinc-500">({counts[f]})</span>
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {filtered.length === 0 && (
          <p data-testid="testimony-empty" className="text-zinc-500 text-sm">No testimonies in this view.</p>
        )}
        {filtered.map((t, i) => (
          <div
            key={t.id}
            data-testid={`testimony-row-${i}`}
            className={`bg-[#0f0f14] border rounded-sm p-5 ${t.approved ? "border-white/10" : "border-[#ff5c00]/30"}`}
          >
            <div className="flex items-start justify-between gap-4 mb-2 flex-wrap">
              <div className="flex items-start gap-4">
                {t.image_url && (
                  <img
                    src={t.image_url}
                    alt=""
                    className="w-14 h-14 rounded-sm object-cover flex-shrink-0"
                    data-testid={`testimony-photo-${i}`}
                  />
                )}
                <div>
                  <p className="font-display uppercase tracking-tight text-lg">{t.first_name} {t.last_name}</p>
                  <p className="text-[10px] font-mono-accent uppercase tracking-widest text-zinc-500 mt-0.5">
                    {t.category || "General"} · {t.created_at?.slice(0, 10)}
                  </p>
                  <div className="flex flex-wrap gap-3 mt-1 text-xs text-zinc-500">
                    {t.email && <span>{t.email}</span>}
                    {t.phone && <span>{t.phone}</span>}
                    <ShareModeBadge mode={t.share_mode} />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => update(t.id, { approved: !t.approved })}
                  data-testid={`testimony-toggle-approve-${i}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm font-mono-accent text-[10px] uppercase tracking-widest border transition-colors ${
                    t.approved
                      ? "bg-[#22c55e]/10 text-[#22c55e] border-[#22c55e]/30"
                      : "border-white/10 text-zinc-400 hover:border-[#22c55e] hover:text-[#22c55e]"
                  }`}
                >
                  <CheckIcon className="w-3 h-3" /> {t.approved ? "Approved" : "Approve"}
                </button>
                <button
                  onClick={() => update(t.id, { featured: !t.featured })}
                  disabled={!t.approved || t.share_mode === "private"}
                  data-testid={`testimony-toggle-feature-${i}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm font-mono-accent text-[10px] uppercase tracking-widest border transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
                    t.featured
                      ? "bg-[#ff5c00]/15 text-[#ff5c00] border-[#ff5c00]/40"
                      : "border-white/10 text-zinc-400 hover:border-[#ff5c00] hover:text-[#ff5c00]"
                  }`}
                  title={t.share_mode === "private" ? "Submitter chose private — cannot feature" : ""}
                >
                  <Star className="w-3 h-3" /> {t.featured ? "Featured" : "Feature"}
                </button>
                <button
                  onClick={() => downloadCard(t.id)}
                  disabled={!t.approved || t.share_mode === "private"}
                  data-testid={`testimony-download-card-${i}`}
                  title={t.share_mode === "private" ? "Private testimonies can't be shared" : "Download Instagram-ready share card"}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm font-mono-accent text-[10px] uppercase tracking-widest border border-white/10 text-zinc-400 hover:border-[#ff5c00] hover:text-[#ff5c00] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <Download className="w-3 h-3" /> Card
                </button>
                <button
                  onClick={() => remove(t.id)}
                  data-testid={`testimony-delete-${i}`}
                  className="p-1.5 text-zinc-500 hover:text-red-400"
                  aria-label="Delete testimony"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <p className="text-zinc-300 text-sm whitespace-pre-wrap leading-relaxed mt-2">{t.testimony}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

const CONTENT_BLOCKS = [
  {
    key: "home_hero",
    label: "Home — Hero Section",
    description: "The headline area visitors see first.",
    fields: [
      { name: "eyebrow", label: "Eyebrow line", type: "text" },
      { name: "headline_lead", label: "Headline part 1", type: "text" },
      { name: "headline_accent", label: "Headline accent (orange)", type: "text" },
      { name: "headline_tail", label: "Headline part 2", type: "text" },
      { name: "body", label: "Body paragraph", type: "textarea" },
      { name: "image_url", label: "Hero Background Image", type: "image" },
    ],
  },
  {
    key: "home_who_we_are",
    label: "Home — Who We Are",
    description: "About preview block with stats and the photo mosaic.",
    fields: [
      { name: "eyebrow", label: "Eyebrow line", type: "text" },
      { name: "heading_lead", label: "Heading part 1", type: "text" },
      { name: "heading_accent", label: "Heading accent (gradient)", type: "text" },
      { name: "heading_tail", label: "Heading part 2", type: "text" },
      { name: "body", label: "Body paragraph", type: "textarea" },
      { name: "stat1_value", label: "Stat 1 — value", type: "text" },
      { name: "stat1_label", label: "Stat 1 — label", type: "text" },
      { name: "stat2_value", label: "Stat 2 — value", type: "text" },
      { name: "stat2_label", label: "Stat 2 — label", type: "text" },
      { name: "stat3_value", label: "Stat 3 — value", type: "text" },
      { name: "stat3_label", label: "Stat 3 — label", type: "text" },
      { name: "image1", label: "Mosaic Image 1 (top-left)", type: "image" },
      { name: "image2", label: "Mosaic Image 2 (top-right)", type: "image" },
      { name: "image3", label: "Mosaic Image 3 (bottom)", type: "image" },
    ],
  },
  {
    key: "about_page",
    label: "About — Page",
    description: "Hero, mission, approach, and pastor card on /about.",
    fields: [
      { name: "eyebrow", label: "Eyebrow line", type: "text" },
      { name: "headline_lead", label: "Headline part 1", type: "text" },
      { name: "headline_accent", label: "Headline accent (orange)", type: "text" },
      { name: "headline_tail", label: "Headline part 2", type: "text" },
      { name: "intro_body", label: "Intro paragraph", type: "textarea" },
      { name: "mission_heading", label: "Mission heading", type: "text" },
      { name: "mission_body", label: "Mission body", type: "textarea" },
      { name: "approach_heading", label: "Approach heading", type: "text" },
      { name: "approach_body", label: "Approach body", type: "textarea" },
      { name: "pastor_name", label: "Pastor name", type: "text" },
      { name: "pastor_title", label: "Pastor title", type: "text" },
      { name: "pastor_bio", label: "Pastor bio", type: "textarea" },
      { name: "pastor_image", label: "Pastor photo", type: "image" },
    ],
  },
  {
    key: "beliefs_page",
    label: "Beliefs — Page Header",
    description: "Headline and intro on /beliefs.",
    fields: [
      { name: "eyebrow", label: "Eyebrow line", type: "text" },
      { name: "headline_lead", label: "Headline part 1", type: "text" },
      { name: "headline_accent", label: "Headline accent (orange)", type: "text" },
      { name: "headline_tail", label: "Headline part 2", type: "text" },
      { name: "intro_body", label: "Intro paragraph", type: "textarea" },
    ],
  },
];

function SiteContentManager() {
  const [content, setContent] = useState({});

  const load = () => api.get("/content").then((r) => setContent(r.data || {}));
  useEffect(() => { load(); }, []);

  return (
    <Section title="Site Content">
      <p className="text-zinc-400 text-sm mb-6 max-w-2xl">
        Edit the copy that appears on the public site. Changes save instantly and update the live site within seconds.
      </p>
      <div className="space-y-6">
        {CONTENT_BLOCKS.map((block) => (
          <ContentBlockEditor
            key={block.key}
            block={block}
            data={content[block.key] || {}}
            onSaved={load}
          />
        ))}
        <ServiceTimesEditor data={content.home_services || {}} onSaved={load} />
        <FellowshipsEditor data={content.home_fellowships || {}} onSaved={load} />
        <DailyVerseEditor data={content.daily_verse || {}} onSaved={load} />
        <MilestoneEditor data={content.home_milestone || {}} onSaved={load} />
        <ContactInfoEditor data={content.contact_info || {}} onSaved={load} />
        <FaqEditor data={content.contact_faqs || {}} onSaved={load} />
      </div>
    </Section>
  );
}

function DailyVerseEditor({ data, onSaved }) {
  const [form, setForm] = useState({ enabled: true, eyebrow: "", text: "", reference: "" });
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  useEffect(() => {
    setForm({
      enabled: data.enabled !== false,
      eyebrow: data.eyebrow ?? "",
      text: data.text ?? "",
      reference: data.reference ?? "",
    });
  }, [data]);

  const save = async (e) => {
    e.preventDefault(); setSaving(true); setStatus("");
    try { await api.put(`/admin/content/daily_verse`, { data: form }); setStatus("Saved ✓"); onSaved(); setTimeout(() => setStatus(""), 1800); }
    catch { setStatus("Save failed"); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    if (!window.confirm("Reset the Daily Verse banner to defaults?")) return;
    try { await api.post(`/admin/content/daily_verse/reset`); onSaved(); setStatus("Reset"); setTimeout(() => setStatus(""), 1800); } catch { /* noop */ }
  };

  return (
    <form onSubmit={save} data-testid="content-block-daily_verse" className="bg-[#0f0f14] border border-white/10 rounded-sm p-6">
      <div className="flex items-start justify-between mb-5">
        <div>
          <h3 className="font-display text-xl uppercase tracking-tight">Home — Today&apos;s Word</h3>
          <p className="text-xs text-zinc-500 mt-1">A daily Bible verse banner that runs across the top of the Home page.</p>
        </div>
        <button type="button" onClick={reset} data-testid="content-reset-daily_verse" className="inline-flex items-center gap-1.5 text-zinc-500 hover:text-[#ff5c00] font-mono-accent text-[10px] uppercase tracking-widest">
          <RotateCcw className="w-3 h-3" /> Reset
        </button>
      </div>
      <label className="flex items-center gap-2 mb-4">
        <input type="checkbox" checked={!!form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="content-daily_verse-enabled" className="w-4 h-4 accent-[#ff5c00]" />
        <span className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-zinc-300">Show banner on the public site</span>
      </label>
      <div className="grid sm:grid-cols-3 gap-4 mb-4">
        <Input label="Eyebrow" value={form.eyebrow} onChange={(e) => setForm({ ...form, eyebrow: e.target.value })} data-testid="content-daily_verse-eyebrow" />
        <Input label="Reference (e.g. Matthew 5:14)" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="content-daily_verse-reference" />
      </div>
      <Textarea label="Verse text" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} rows={3} data-testid="content-daily_verse-text" />

      <div className="flex items-center gap-3 mt-5 pt-4 border-t border-white/5">
        <button type="submit" disabled={saving} data-testid="content-save-daily_verse" className="inline-flex items-center gap-2 bg-[#ff5c00] hover:bg-[#ff7a33] disabled:opacity-60 text-white font-mono-accent text-xs uppercase tracking-widest px-5 py-2.5 rounded-sm">
          <CheckIcon className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save Changes"}
        </button>
        {status && <span className="text-[11px] font-mono-accent uppercase tracking-widest text-[#22c55e]">{status}</span>}
      </div>
    </form>
  );
}

function MilestoneEditor({ data, onSaved }) {
  const [form, setForm] = useState({ eyebrow: "", value: "", label: "", body: "" });
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  useEffect(() => {
    setForm({
      eyebrow: data.eyebrow ?? "",
      value: data.value ?? "",
      label: data.label ?? "",
      body: data.body ?? "",
    });
  }, [data]);

  const save = async (e) => {
    e.preventDefault(); setSaving(true); setStatus("");
    try { await api.put(`/admin/content/home_milestone`, { data: form }); setStatus("Saved ✓"); onSaved(); setTimeout(() => setStatus(""), 1800); }
    catch { setStatus("Save failed"); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    if (!window.confirm("Reset the milestone stat to defaults?")) return;
    try { await api.post(`/admin/content/home_milestone/reset`); onSaved(); setStatus("Reset"); setTimeout(() => setStatus(""), 1800); } catch { /* noop */ }
  };

  return (
    <form onSubmit={save} data-testid="content-block-home_milestone" className="bg-[#0f0f14] border border-white/10 rounded-sm p-6">
      <div className="flex items-start justify-between mb-5">
        <div>
          <h3 className="font-display text-xl uppercase tracking-tight">Home / About — Years of Ministry</h3>
          <p className="text-xs text-zinc-500 mt-1">Big number + label shown on the Home page and About page (e.g. &ldquo;13+ Years of Ministry&rdquo;).</p>
        </div>
        <button type="button" onClick={reset} data-testid="content-reset-home_milestone" className="inline-flex items-center gap-1.5 text-zinc-500 hover:text-[#ff5c00] font-mono-accent text-[10px] uppercase tracking-widest">
          <RotateCcw className="w-3 h-3" /> Reset
        </button>
      </div>
      <div className="grid sm:grid-cols-3 gap-4 mb-4">
        <Input label="Eyebrow" value={form.eyebrow} onChange={(e) => setForm({ ...form, eyebrow: e.target.value })} data-testid="content-milestone-eyebrow" />
        <Input label="Big value (e.g. 13+)" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="content-milestone-value" />
        <Input label="Label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="content-milestone-label" />
      </div>
      <Textarea label="Supporting sentence" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={2} data-testid="content-milestone-body" />
      <div className="flex items-center gap-3 mt-5 pt-4 border-t border-white/5">
        <button type="submit" disabled={saving} data-testid="content-save-home_milestone" className="inline-flex items-center gap-2 bg-[#ff5c00] hover:bg-[#ff7a33] disabled:opacity-60 text-white font-mono-accent text-xs uppercase tracking-widest px-5 py-2.5 rounded-sm">
          <CheckIcon className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save Changes"}
        </button>
        {status && <span className="text-[11px] font-mono-accent uppercase tracking-widest text-[#22c55e]">{status}</span>}
      </div>
    </form>
  );
}

function ContactInfoEditor({ data, onSaved }) {
  const [form, setForm] = useState({ address_line1: "", address_line2: "", address_line3: "", email: "", phone: "", whatsapp: "", map_query: "", map_link: "" });
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  useEffect(() => {
    setForm({
      address_line1: data.address_line1 ?? "",
      address_line2: data.address_line2 ?? "",
      address_line3: data.address_line3 ?? "",
      email: data.email ?? "",
      phone: data.phone ?? "",
      whatsapp: data.whatsapp ?? "",
      map_query: data.map_query ?? "",
      map_link: data.map_link ?? "",
    });
  }, [data]);

  const save = async (e) => {
    e.preventDefault(); setSaving(true); setStatus("");
    try { await api.put(`/admin/content/contact_info`, { data: form }); setStatus("Saved ✓"); onSaved(); setTimeout(() => setStatus(""), 1800); }
    catch { setStatus("Save failed"); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    if (!window.confirm("Reset church contact info to defaults?")) return;
    try { await api.post(`/admin/content/contact_info/reset`); onSaved(); setStatus("Reset"); setTimeout(() => setStatus(""), 1800); } catch { /* noop */ }
  };

  return (
    <form onSubmit={save} data-testid="content-block-contact_info" className="bg-[#0f0f14] border border-white/10 rounded-sm p-6">
      <div className="flex items-start justify-between mb-5">
        <div>
          <h3 className="font-display text-xl uppercase tracking-tight">Church Contact Info</h3>
          <p className="text-xs text-zinc-500 mt-1">Address, email, phone & WhatsApp. Drives the Contact page sidebar, Footer block, and Google Map embed.</p>
        </div>
        <button type="button" onClick={reset} data-testid="content-reset-contact_info" className="inline-flex items-center gap-1.5 text-zinc-500 hover:text-[#ff5c00] font-mono-accent text-[10px] uppercase tracking-widest">
          <RotateCcw className="w-3 h-3" /> Reset
        </button>
      </div>
      <div className="grid gap-4 mb-4">
        <Input label="Address — line 1" value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} data-testid="content-contact-line1" />
        <Input label="Address — line 2" value={form.address_line2} onChange={(e) => setForm({ ...form, address_line2: e.target.value })} data-testid="content-contact-line2" />
        <Input label="Address — line 3 (city / country)" value={form.address_line3} onChange={(e) => setForm({ ...form, address_line3: e.target.value })} data-testid="content-contact-line3" />
      </div>
      <div className="grid sm:grid-cols-3 gap-4 mb-4">
        <Input label="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="content-contact-email" />
        <Input label="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="content-contact-phone" />
        <Input label="WhatsApp (e.g. +971…)" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} data-testid="content-contact-whatsapp" />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Input label="Map search query" value={form.map_query} onChange={(e) => setForm({ ...form, map_query: e.target.value })} data-testid="content-contact-mapquery" />
        <Input label="Google Maps deep link" value={form.map_link} onChange={(e) => setForm({ ...form, map_link: e.target.value })} data-testid="content-contact-maplink" />
      </div>
      <div className="flex items-center gap-3 mt-5 pt-4 border-t border-white/5">
        <button type="submit" disabled={saving} data-testid="content-save-contact_info" className="inline-flex items-center gap-2 bg-[#ff5c00] hover:bg-[#ff7a33] disabled:opacity-60 text-white font-mono-accent text-xs uppercase tracking-widest px-5 py-2.5 rounded-sm">
          <CheckIcon className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save Changes"}
        </button>
        {status && <span className="text-[11px] font-mono-accent uppercase tracking-widest text-[#22c55e]">{status}</span>}
      </div>
    </form>
  );
}

function FaqEditor({ data, onSaved }) {
  const [form, setForm] = useState({ eyebrow: "", heading_lead: "", heading_accent: "", items: [] });
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");

  useEffect(() => {
    setForm({
      eyebrow: data.eyebrow ?? "",
      heading_lead: data.heading_lead ?? "",
      heading_accent: data.heading_accent ?? "",
      items: Array.isArray(data.items) ? data.items.map((s) => ({ ...s })) : [],
    });
  }, [data]);

  const setItem = (i, patch) => {
    const items = [...form.items]; items[i] = { ...items[i], ...patch }; setForm({ ...form, items });
  };
  const addItem = () => setForm({ ...form, items: [...form.items, { q: "", a: "" }] });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const move = (i, dir) => {
    const next = i + dir; if (next < 0 || next >= form.items.length) return;
    const items = [...form.items]; [items[i], items[next]] = [items[next], items[i]]; setForm({ ...form, items });
  };

  const save = async (e) => {
    e.preventDefault(); setSaving(true); setStatus("");
    try { await api.put(`/admin/content/contact_faqs`, { data: form }); setStatus("Saved ✓"); onSaved(); setTimeout(() => setStatus(""), 1800); }
    catch { setStatus("Save failed"); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    if (!window.confirm("Reset FAQs to the defaults?")) return;
    try { await api.post(`/admin/content/contact_faqs/reset`); onSaved(); setStatus("Reset"); setTimeout(() => setStatus(""), 1800); } catch { /* noop */ }
  };

  return (
    <form onSubmit={save} data-testid="content-block-contact_faqs" className="bg-[#0f0f14] border border-white/10 rounded-sm p-6">
      <div className="flex items-start justify-between mb-5">
        <div>
          <h3 className="font-display text-xl uppercase tracking-tight">Contact — FAQs</h3>
          <p className="text-xs text-zinc-500 mt-1">Questions & answers shown at the bottom of the Contact page.</p>
        </div>
        <button type="button" onClick={reset} data-testid="content-reset-contact_faqs" className="inline-flex items-center gap-1.5 text-zinc-500 hover:text-[#ff5c00] font-mono-accent text-[10px] uppercase tracking-widest">
          <RotateCcw className="w-3 h-3" /> Reset
        </button>
      </div>
      <div className="grid sm:grid-cols-3 gap-4 mb-4">
        <Input label="Eyebrow" value={form.eyebrow} onChange={(e) => setForm({ ...form, eyebrow: e.target.value })} data-testid="content-faq-eyebrow" />
        <Input label="Heading (lead)" value={form.heading_lead} onChange={(e) => setForm({ ...form, heading_lead: e.target.value })} data-testid="content-faq-heading-lead" />
        <Input label="Heading (accent)" value={form.heading_accent} onChange={(e) => setForm({ ...form, heading_accent: e.target.value })} data-testid="content-faq-heading-accent" />
      </div>

      <div className="space-y-3 mt-3">
        {form.items.map((it, i) => (
          <div key={i} data-testid={`faq-row-${i}`} className="bg-[#0a0a0d] border border-white/10 rounded-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="font-mono-accent text-[10px] uppercase tracking-widest text-zinc-500">FAQ #{i + 1}</span>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`faq-up-${i}`} className="text-zinc-400 hover:text-[#ff5c00] disabled:opacity-30 p-1.5"><ArrowUp className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === form.items.length - 1} data-testid={`faq-down-${i}`} className="text-zinc-400 hover:text-[#ff5c00] disabled:opacity-30 p-1.5"><ArrowDown className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => removeItem(i)} data-testid={`faq-remove-${i}`} className="text-zinc-500 hover:text-red-400 p-1.5"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            <Input label="Question" value={it.q || ""} onChange={(e) => setItem(i, { q: e.target.value })} data-testid={`faq-q-${i}`} />
            <div className="mt-3">
              <Textarea label="Answer" value={it.a || ""} onChange={(e) => setItem(i, { a: e.target.value })} rows={3} data-testid={`faq-a-${i}`} />
            </div>
          </div>
        ))}
      </div>
      <button type="button" onClick={addItem} data-testid="faq-add" className="mt-4 inline-flex items-center gap-1 text-[#ff5c00] hover:text-white font-mono-accent text-[10px] uppercase tracking-widest">
        <Plus className="w-3 h-3" /> Add FAQ
      </button>

      <div className="flex items-center gap-3 mt-5 pt-4 border-t border-white/5">
        <button type="submit" disabled={saving} data-testid="content-save-contact_faqs" className="inline-flex items-center gap-2 bg-[#ff5c00] hover:bg-[#ff7a33] disabled:opacity-60 text-white font-mono-accent text-xs uppercase tracking-widest px-5 py-2.5 rounded-sm">
          <CheckIcon className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save Changes"}
        </button>
        {status && <span className="text-[11px] font-mono-accent uppercase tracking-widest text-[#22c55e]">{status}</span>}
      </div>
    </form>
  );
}

function ServiceTimesEditor({ data, onSaved }) {
  const [form, setForm] = useState({ eyebrow: "", heading: "", items: [] });
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");

  useEffect(() => {
    setForm({
      eyebrow: data.eyebrow ?? "",
      heading: data.heading ?? "",
      items: Array.isArray(data.items) ? data.items.map((s) => ({ ...s })) : [],
    });
  }, [data]);

  const setItem = (i, patch) => {
    const items = [...form.items];
    items[i] = { ...items[i], ...patch };
    setForm({ ...form, items });
  };
  const addItem = () => setForm({ ...form, items: [...form.items, { day: "", name: "", time: "", note: "" }] });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const move = (i, dir) => {
    const next = i + dir;
    if (next < 0 || next >= form.items.length) return;
    const items = [...form.items];
    [items[i], items[next]] = [items[next], items[i]];
    setForm({ ...form, items });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setStatus("");
    try {
      await api.put(`/admin/content/home_services`, { data: form });
      setStatus("Saved ✓");
      onSaved();
      setTimeout(() => setStatus(""), 1800);
    } catch (err) {
      setStatus("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset service times to the defaults?")) return;
    try {
      await api.post(`/admin/content/home_services/reset`);
      onSaved();
      setStatus("Reset to default");
      setTimeout(() => setStatus(""), 1800);
    } catch (e) { /* noop */ }
  };

  return (
    <form onSubmit={save} data-testid="content-block-home_services" className="bg-[#0f0f14] border border-white/10 rounded-sm p-6">
      <div className="flex items-start justify-between mb-5">
        <div>
          <h3 className="font-display text-xl uppercase tracking-tight">Home — Service Times</h3>
          <p className="text-xs text-zinc-500 mt-1">Service / gathering schedule shown on the home page. Reorder with the arrows.</p>
        </div>
        <button
          type="button"
          onClick={reset}
          data-testid="content-reset-home_services"
          className="inline-flex items-center gap-1.5 text-zinc-500 hover:text-[#ff5c00] font-mono-accent text-[10px] uppercase tracking-widest"
        >
          <RotateCcw className="w-3 h-3" /> Reset
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-5">
        <Input
          label="Eyebrow"
          value={form.eyebrow}
          onChange={(e) => setForm({ ...form, eyebrow: e.target.value })}
          data-testid="content-home_services-eyebrow"
        />
        <Input
          label="Heading"
          value={form.heading}
          onChange={(e) => setForm({ ...form, heading: e.target.value })}
          data-testid="content-home_services-heading"
        />
      </div>

      <div className="space-y-3">
        {form.items.map((item, i) => (
          <div key={i} data-testid={`service-time-row-${i}`} className="bg-[#0a0a0d] border border-white/10 rounded-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="font-mono-accent text-[10px] uppercase tracking-widest text-zinc-500">Service #{i + 1}</span>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`service-time-up-${i}`} className="text-zinc-400 hover:text-[#ff5c00] disabled:opacity-30 p-1.5"><ArrowUp className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === form.items.length - 1} data-testid={`service-time-down-${i}`} className="text-zinc-400 hover:text-[#ff5c00] disabled:opacity-30 p-1.5"><ArrowDown className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => removeItem(i)} data-testid={`service-time-remove-${i}`} className="text-zinc-500 hover:text-red-400 p-1.5"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Input label="Day" value={item.day || ""} onChange={(e) => setItem(i, { day: e.target.value })} data-testid={`service-time-day-${i}`} />
              <Input label="Name" value={item.name || ""} onChange={(e) => setItem(i, { name: e.target.value })} data-testid={`service-time-name-${i}`} />
              <Input label="Time" value={item.time || ""} onChange={(e) => setItem(i, { time: e.target.value })} data-testid={`service-time-time-${i}`} />
              <Input label="Note" value={item.note || ""} onChange={(e) => setItem(i, { note: e.target.value })} data-testid={`service-time-note-${i}`} />
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={addItem}
        data-testid="service-time-add"
        className="mt-4 inline-flex items-center gap-1 text-[#ff5c00] hover:text-white font-mono-accent text-[10px] uppercase tracking-widest"
      >
        <Plus className="w-3 h-3" /> Add Service
      </button>

      <div className="flex items-center gap-3 mt-5 pt-4 border-t border-white/5">
        <button
          type="submit"
          disabled={saving}
          data-testid="content-save-home_services"
          className="inline-flex items-center gap-2 bg-[#ff5c00] hover:bg-[#ff7a33] disabled:opacity-60 text-white font-mono-accent text-xs uppercase tracking-widest px-5 py-2.5 rounded-sm"
        >
          <CheckIcon className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save Changes"}
        </button>
        {status && <span className="text-[11px] font-mono-accent uppercase tracking-widest text-[#22c55e]">{status}</span>}
      </div>
    </form>
  );
}

function ImageField({ label, value, onChange, testId, position, onPositionChange, scale, onScaleChange }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { setError("Image only."); return; }
    if (file.size > 8 * 1024 * 1024) { setError("Max 8 MB."); return; }
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(r.data.url);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const positions = [
    ["top left", "top", "top right"],
    ["left", "center", "right"],
    ["bottom left", "bottom", "bottom right"],
  ];

  const previewStyle = {
    objectPosition: position || "center",
    transform: `scale(${scale || 1})`,
    transformOrigin: position || "center",
  };

  return (
    <div data-testid={`${testId}-field`}>
      <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2 block">
        {label}
      </span>
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-1 aspect-square rounded-sm overflow-hidden bg-[#050505] border border-white/10 flex items-center justify-center">
          {value ? (
            <img src={value} alt="" style={previewStyle} className="w-full h-full object-cover transition-transform" data-testid={`${testId}-preview`} />
          ) : (
            <ImageIcon className="w-7 h-7 text-zinc-700" />
          )}
        </div>
        <div className="col-span-2 space-y-2">
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Paste image URL…"
            data-testid={`${testId}-url`}
            className="w-full bg-[#050505] border border-white/10 px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#ff5c00] rounded-sm"
          />
          <div className="flex flex-wrap items-center gap-2">
            <label
              data-testid={`${testId}-upload`}
              className="inline-flex items-center gap-2 border border-white/10 hover:border-[#ff5c00] hover:text-[#ff5c00] text-zinc-300 font-mono-accent text-[10px] uppercase tracking-widest px-3 py-2 rounded-sm cursor-pointer"
            >
              <Upload className="w-3 h-3" /> {uploading ? "Uploading…" : "Upload File"}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => upload(e.target.files?.[0])}
                disabled={uploading}
                className="hidden"
              />
            </label>
            {value && (
              <button
                type="button"
                onClick={() => onChange("")}
                data-testid={`${testId}-clear`}
                className="font-mono-accent text-[10px] uppercase tracking-widest text-zinc-500 hover:text-red-400"
              >
                Clear
              </button>
            )}
            {error && <span className="text-red-400 text-[11px]">{error}</span>}
          </div>
        </div>
      </div>

      {value && onPositionChange && (
        <div className="mt-3 pt-3 border-t border-white/5 grid sm:grid-cols-2 gap-4">
          <div>
            <p className="font-mono-accent text-[10px] tracking-widest uppercase text-zinc-500 mb-2 flex items-center gap-1"><Crop className="w-3 h-3" /> Focus point</p>
            <div className="inline-grid grid-cols-3 gap-1 bg-[#0a0a0d] border border-white/10 p-1 rounded-sm">
              {positions.flat().map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => onPositionChange(p)}
                  data-testid={`${testId}-pos-${p.replace(" ", "-")}`}
                  className={`w-7 h-7 rounded-sm transition-colors ${
                    (position || "center") === p
                      ? "bg-[#ff5c00] border border-[#ff5c00]"
                      : "border border-white/10 hover:border-[#ff5c00] bg-[#050505]"
                  }`}
                  aria-label={p}
                />
              ))}
            </div>
          </div>
          {onScaleChange && (
            <div>
              <p className="font-mono-accent text-[10px] tracking-widest uppercase text-zinc-500 mb-2 flex items-center gap-1">
                <ZoomIn className="w-3 h-3" /> Zoom · {(scale || 1).toFixed(2)}x
              </p>
              <input
                type="range"
                min="0.6"
                max="2.0"
                step="0.05"
                value={scale || 1}
                onChange={(e) => onScaleChange(parseFloat(e.target.value))}
                data-testid={`${testId}-scale`}
                className="w-full accent-[#ff5c00]"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ContentBlockEditor({ block, data, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");

  useEffect(() => {
    const initial = {};
    block.fields.forEach((f) => { initial[f.name] = data[f.name] ?? ""; });
    setForm(initial);
  }, [data, block]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setStatus("");
    try {
      await api.put(`/admin/content/${block.key}`, { data: form });
      setStatus("Saved ✓");
      onSaved();
      setTimeout(() => setStatus(""), 1800);
    } catch (err) {
      setStatus("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset this section to the original copy?")) return;
    try {
      await api.post(`/admin/content/${block.key}/reset`);
      onSaved();
      setStatus("Reset to default");
      setTimeout(() => setStatus(""), 1800);
    } catch (e) {}
  };

  return (
    <form onSubmit={save} data-testid={`content-block-${block.key}`} className="bg-[#0f0f14] border border-white/10 rounded-sm p-6">
      <div className="flex items-start justify-between mb-5">
        <div>
          <h3 className="font-display text-xl uppercase tracking-tight">{block.label}</h3>
          <p className="text-xs text-zinc-500 mt-1">{block.description}</p>
        </div>
        <button
          type="button"
          onClick={reset}
          data-testid={`content-reset-${block.key}`}
          className="inline-flex items-center gap-1.5 text-zinc-500 hover:text-[#ff5c00] font-mono-accent text-[10px] uppercase tracking-widest"
        >
          <RotateCcw className="w-3 h-3" /> Reset
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {block.fields.map((f) => (
          <div key={f.name} className={f.type === "textarea" || f.type === "image" ? "sm:col-span-2" : ""}>
            {f.type === "textarea" ? (
              <Textarea
                label={f.label}
                rows={3}
                value={form[f.name] ?? ""}
                onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                data-testid={`content-${block.key}-${f.name}`}
              />
            ) : f.type === "image" ? (
              <ImageField
                label={f.label}
                value={form[f.name] ?? ""}
                onChange={(v) => setForm({ ...form, [f.name]: v })}
                position={form[`${f.name}_position`] || "center"}
                onPositionChange={(p) => setForm({ ...form, [`${f.name}_position`]: p })}
                scale={form[`${f.name}_scale`] || 1}
                onScaleChange={(s) => setForm({ ...form, [`${f.name}_scale`]: s })}
                testId={`content-${block.key}-${f.name}`}
              />
            ) : (
              <Input
                label={f.label}
                value={form[f.name] ?? ""}
                onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                data-testid={`content-${block.key}-${f.name}`}
              />
            )}
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 mt-5 pt-4 border-t border-white/5">
        <button
          type="submit"
          disabled={saving}
          data-testid={`content-save-${block.key}`}
          className="inline-flex items-center gap-2 bg-[#ff5c00] hover:bg-[#ff7a33] disabled:opacity-60 text-white font-mono-accent text-xs uppercase tracking-widest px-5 py-2.5 rounded-sm"
        >
          <CheckIcon className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save Changes"}
        </button>
        {status && <span className="text-[11px] font-mono-accent uppercase tracking-widest text-[#22c55e]">{status}</span>}
      </div>
    </form>
  );
}

function VisitorList() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/visitors").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const markContacted = async (id) => { await api.put(`/admin/visitors/${id}/contacted`); load(); };
  return (
    <Section title="First-Time Visitors">
      <div className="space-y-3">
        {items.length === 0 && <p className="text-zinc-500">No visitor sign-ups yet.</p>}
        {items.map((v, i) => (
          <div
            key={v.id}
            data-testid={`visitor-row-${i}`}
            className={`bg-[#0f0f14] border rounded-sm p-5 ${v.contacted ? "border-white/5 opacity-60" : "border-white/10"}`}
          >
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <p className="font-display text-lg uppercase tracking-tight">
                {v.name}
                {!v.contacted && <span className="ml-2 text-[10px] px-2 py-0.5 bg-[#ff5c00]/20 text-[#ff5c00] rounded-sm font-mono-accent uppercase tracking-widest">NEW</span>}
                {v.contacted && <span className="ml-2 text-[10px] px-2 py-0.5 bg-[#22c55e]/20 text-[#22c55e] rounded-sm font-mono-accent uppercase tracking-widest">CONTACTED</span>}
              </p>
              <p className="text-[10px] font-mono-accent uppercase tracking-widest text-zinc-500">{v.created_at?.slice(0, 10)}</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-zinc-300 mb-3">
              <p><span className="text-zinc-500 font-mono-accent text-[10px] uppercase tracking-widest mr-2">Email</span>{v.email}</p>
              {v.phone && <p><span className="text-zinc-500 font-mono-accent text-[10px] uppercase tracking-widest mr-2">Phone</span>{v.phone}</p>}
              {v.planned_date && <p><span className="text-zinc-500 font-mono-accent text-[10px] uppercase tracking-widest mr-2">Visit Date</span>{v.planned_date}</p>}
              <p><span className="text-zinc-500 font-mono-accent text-[10px] uppercase tracking-widest mr-2">Party Size</span>{v.party_size}</p>
              {v.how_heard && <p><span className="text-zinc-500 font-mono-accent text-[10px] uppercase tracking-widest mr-2">Heard Via</span>{v.how_heard}</p>}
            </div>
            {v.notes && <p className="text-zinc-300 text-sm whitespace-pre-wrap border-t border-white/5 pt-3">{v.notes}</p>}
            {!v.contacted && (
              <div className="flex justify-end mt-3">
                <button
                  onClick={() => markContacted(v.id)}
                  data-testid={`visitor-mark-contacted-${i}`}
                  className="bg-[#22c55e]/10 hover:bg-[#22c55e]/20 text-[#22c55e] px-3 py-1.5 rounded-sm font-mono-accent text-xs uppercase tracking-widest"
                >
                  Mark Contacted
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
}

function SundaySchoolAdmin() {
  const [sub, setSub] = useState("attendance");
  return (
    <Section title="Sunday School">
      <div className="flex gap-2 mb-6">
        {[
          { id: "attendance", label: "Attendance" },
          { id: "students", label: "Students" },
          { id: "classes", label: "Classes" },
          { id: "summary", label: "Quarterly" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setSub(t.id)}
            data-testid={`ss-admin-tab-${t.id}`}
            className={`font-mono-accent text-xs tracking-[0.18em] uppercase px-4 py-2 rounded-sm border ${
              sub === t.id
                ? "border-[#ff5c00] text-white bg-[#ff5c00]/10"
                : "border-white/10 text-zinc-400 hover:text-white"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {sub === "attendance" && <SSAttendanceView />}
      {sub === "students" && <SSStudentsView />}
      {sub === "classes" && <SSClassesView />}
      {sub === "summary" && <SSSummaryView />}
    </Section>
  );
}

function todayDateStr() {
  return new Date().toISOString().slice(0, 10);
}

function SSAttendanceView() {
  const [date, setDate] = useState(todayDateStr());
  const [classes, setClasses] = useState([]);
  const [classFilter, setClassFilter] = useState("");
  const [rows, setRows] = useState([]);

  const load = async () => {
    const params = { sunday_date: date };
    if (classFilter) params.class_id = classFilter;
    const r = await api.get("/admin/ss/attendance", { params });
    setRows(r.data);
  };
  useEffect(() => { api.get("/admin/ss/classes").then((r) => setClasses(r.data)); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [date, classFilter]);

  return (
    <div data-testid="ss-attendance-view">
      <div className="flex flex-wrap items-end gap-4 mb-5 bg-[#0f0f14] border border-white/10 p-5 rounded-sm">
        <label className="block">
          <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-1.5 block">Sunday Date</span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid="ss-attendance-date"
            className="bg-[#0a0a0d] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-[#ff5c00] rounded-sm"
          />
        </label>
        <label className="block">
          <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-1.5 block">Class</span>
          <select
            value={classFilter}
            onChange={(e) => setClassFilter(e.target.value)}
            data-testid="ss-attendance-class-filter"
            className="bg-[#0a0a0d] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-[#ff5c00] rounded-sm"
          >
            <option value="">All Classes</option>
            {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <p className="ml-auto font-mono-accent text-xs tracking-widest text-zinc-400">
          {rows.length} present
        </p>
      </div>

      <div className="bg-[#0f0f14] border border-white/10 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#080808] text-zinc-500 font-mono-accent text-[10px] uppercase tracking-widest">
            <tr>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Class</th>
              <th className="text-left p-3">Phone</th>
              <th className="text-left p-3">Marked At</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id} data-testid={`ss-attendance-row-${i}`} className="border-t border-white/5">
                <td className="p-3 text-white">{r.student_name}</td>
                <td className="p-3 text-zinc-300">{r.class_name}</td>
                <td className="p-3 text-zinc-400 font-mono-accent text-xs">{r.student_phone}</td>
                <td className="p-3 text-zinc-500 font-mono-accent text-xs">{r.created_at?.slice(11, 16)}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={4} className="p-6 text-center text-zinc-500">No attendance for this Sunday yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SSStudentsView() {
  const [rows, setRows] = useState([]);
  const [classes, setClasses] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const load = () => api.get("/admin/ss/students").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    api.get("/admin/ss/classes").then((r) => setClasses(r.data));
  }, []);
  const remove = async (id) => {
    if (!window.confirm("Remove this student and all their attendance? This cannot be undone.")) return;
    await api.delete(`/admin/ss/students/${id}`);
    load();
  };

  return (
    <div data-testid="ss-students-view" className="bg-[#0f0f14] border border-white/10 rounded-sm overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-[#080808] text-zinc-500 font-mono-accent text-[10px] uppercase tracking-widest">
          <tr>
            <th className="text-left p-3">Name</th>
            <th className="text-left p-3">Class</th>
            <th className="text-left p-3">Phone</th>
            <th className="text-left p-3">Registered</th>
            <th className="text-right p-3"> </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s, i) =>
            editingId === s.id ? (
              <SSStudentEditRow
                key={s.id}
                index={i}
                student={s}
                classes={classes}
                onCancel={() => setEditingId(null)}
                onSaved={() => { setEditingId(null); load(); }}
              />
            ) : (
              <tr key={s.id} data-testid={`ss-student-row-${i}`} className="border-t border-white/5">
                <td className="p-3 text-white">{s.name}</td>
                <td className="p-3 text-zinc-300">{s.class_name || "—"}</td>
                <td className="p-3 text-zinc-400 font-mono-accent text-xs">{s.phone}</td>
                <td className="p-3 text-zinc-500 font-mono-accent text-xs">{(s.registered_at || "").slice(0, 10)}</td>
                <td className="p-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button onClick={() => setEditingId(s.id)} data-testid={`ss-student-edit-${i}`} className="text-zinc-500 hover:text-[#ff5c00] p-1.5" aria-label="Edit">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(s.id)} data-testid={`ss-student-delete-${i}`} className="text-zinc-500 hover:text-red-400 p-1.5" aria-label="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            )
          )}
          {rows.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-zinc-500">No registered students yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function SSStudentEditRow({ index, student, classes, onCancel, onSaved }) {
  const [form, setForm] = useState({
    name: student.name || "",
    phone: student.phone || "",
    class_id: student.class_id || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await api.put(`/admin/ss/students/${student.id}`, form);
      onSaved();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not save.");
      setSaving(false);
    }
  };

  return (
    <tr data-testid={`ss-student-edit-row-${index}`} className="border-t border-[#ff5c00]/40 bg-[#0a0a0d]">
      <td className="p-2">
        <input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid={`ss-student-edit-name-${index}`}
          className="w-full bg-[#050505] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-[#ff5c00] rounded-sm"
        />
      </td>
      <td className="p-2">
        <select
          value={form.class_id}
          onChange={(e) => setForm({ ...form, class_id: e.target.value })}
          data-testid={`ss-student-edit-class-${index}`}
          className="w-full bg-[#050505] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-[#ff5c00] rounded-sm"
        >
          {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </td>
      <td className="p-2">
        <input
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          data-testid={`ss-student-edit-phone-${index}`}
          className="w-full bg-[#050505] border border-white/10 px-3 py-2 text-sm text-white font-mono-accent focus:outline-none focus:border-[#ff5c00] rounded-sm"
        />
      </td>
      <td className="p-3 text-zinc-500 font-mono-accent text-xs">{(student.registered_at || "").slice(0, 10)}</td>
      <td className="p-3 text-right">
        <div className="inline-flex items-center gap-1">
          <button onClick={save} disabled={saving} data-testid={`ss-student-save-${index}`} className="text-[#22c55e] hover:text-[#22c55e]/80 p-1.5 disabled:opacity-50" aria-label="Save">
            <CheckIcon className="w-4 h-4" />
          </button>
          <button onClick={onCancel} data-testid={`ss-student-cancel-${index}`} className="text-zinc-500 hover:text-white p-1.5" aria-label="Cancel">
            <X className="w-4 h-4" />
          </button>
        </div>
        {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
      </td>
    </tr>
  );
}

function SSClassesView() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", teacher: "", description: "" });
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const load = () => api.get("/admin/ss/classes").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const add = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/admin/ss/classes", form);
      setForm({ name: "", teacher: "", description: "" });
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this class? Students still in it will need to be reassigned.")) return;
    await api.delete(`/admin/ss/classes/${id}`);
    load();
  };
  return (
    <div data-testid="ss-classes-view">
      <form onSubmit={add} data-testid="ss-class-form" className="bg-[#0f0f14] border border-white/10 rounded-sm p-6 mb-6 grid sm:grid-cols-3 gap-4">
        <Input label="Class Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ss-class-name" />
        <Input label="Teacher" value={form.teacher} onChange={(e) => setForm({ ...form, teacher: e.target.value })} data-testid="ss-class-teacher" />
        <Input label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="ss-class-desc" />
        {error && <p className="text-red-400 text-sm sm:col-span-3">{error}</p>}
        <div className="sm:col-span-3">
          <button data-testid="ss-class-create" className="inline-flex items-center gap-2 bg-[#ff5c00] hover:bg-[#ff7a33] text-white font-mono-accent text-xs uppercase tracking-widest px-5 py-3 rounded-sm">
            <Plus className="w-4 h-4" /> Add Class
          </button>
        </div>
      </form>
      <div className="space-y-2">
        {rows.map((c, i) =>
          editingId === c.id ? (
            <SSClassEditRow
              key={c.id}
              index={i}
              cls={c}
              onCancel={() => setEditingId(null)}
              onSaved={() => { setEditingId(null); load(); }}
            />
          ) : (
            <div key={c.id} data-testid={`ss-class-row-${i}`} className="flex items-start justify-between gap-4 bg-[#0f0f14] border border-white/10 rounded-sm p-4">
              <div className="flex-1 min-w-0">
                <p className="font-display text-lg uppercase tracking-tight">{c.name}</p>
                <p className="text-xs text-zinc-500 font-mono-accent uppercase tracking-widest">{c.teacher || "—"}</p>
                {c.description && <p className="text-sm text-zinc-400 mt-1">{c.description}</p>}
              </div>
              <div className="inline-flex items-center gap-1 shrink-0">
                <button onClick={() => setEditingId(c.id)} data-testid={`ss-class-edit-${i}`} className="text-zinc-500 hover:text-[#ff5c00] p-2" aria-label="Edit">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => remove(c.id)} data-testid={`ss-class-delete-${i}`} className="text-zinc-500 hover:text-red-400 p-2" aria-label="Delete">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
}

function SSClassEditRow({ index, cls, onCancel, onSaved }) {
  const [form, setForm] = useState({
    name: cls.name || "",
    teacher: cls.teacher || "",
    description: cls.description || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api.put(`/admin/ss/classes/${cls.id}`, form);
      onSaved();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not save.");
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={save}
      data-testid={`ss-class-edit-row-${index}`}
      className="bg-[#0a0a0d] border border-[#ff5c00]/40 rounded-sm p-5 grid sm:grid-cols-3 gap-3"
    >
      <Input label="Class Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid={`ss-class-edit-name-${index}`} />
      <Input label="Teacher" value={form.teacher} onChange={(e) => setForm({ ...form, teacher: e.target.value })} data-testid={`ss-class-edit-teacher-${index}`} />
      <Input label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid={`ss-class-edit-desc-${index}`} />
      {error && <p className="text-red-400 text-sm sm:col-span-3">{error}</p>}
      <div className="sm:col-span-3 flex items-center gap-2">
        <button
          type="submit"
          disabled={saving}
          data-testid={`ss-class-save-${index}`}
          className="inline-flex items-center gap-2 bg-[#ff5c00] hover:bg-[#ff7a33] disabled:opacity-60 text-white font-mono-accent text-xs uppercase tracking-widest px-4 py-2 rounded-sm"
        >
          <CheckIcon className="w-3.5 h-3.5" /> Save
        </button>
        <button
          type="button"
          onClick={onCancel}
          data-testid={`ss-class-cancel-${index}`}
          className="inline-flex items-center gap-2 border border-white/10 hover:border-white/30 text-zinc-300 font-mono-accent text-xs uppercase tracking-widest px-4 py-2 rounded-sm"
        >
          <X className="w-3.5 h-3.5" /> Cancel
        </button>
      </div>
    </form>
  );
}

function SSSummaryView() {
  // default to last 90 days (1 quarter)
  const today = todayDateStr();
  const ninety = new Date(); ninety.setDate(ninety.getDate() - 90);
  const [from, setFrom] = useState(ninety.toISOString().slice(0, 10));
  const [to, setTo] = useState(today);
  const [rows, setRows] = useState([]);
  const load = async () => {
    const r = await api.get("/admin/ss/summary", { params: { from_date: from, to_date: to } });
    setRows(r.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [from, to]);

  const exportCsv = () => {
    const lines = [
      "Name,Class,Phone,Attendance Count,Sundays",
      ...rows.map((r) => `"${r.student_name || ""}","${r.class_name || ""}","${r.student_phone || ""}",${r.attendance_count},"${(r.sundays || []).join("; ")}"`),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ss-attendance-${from}_to_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="ss-summary-view">
      <div className="flex flex-wrap items-end gap-4 mb-5 bg-[#0f0f14] border border-white/10 p-5 rounded-sm">
        <label className="block">
          <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-1.5 block">From</span>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="ss-summary-from" className="bg-[#0a0a0d] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-[#ff5c00] rounded-sm" />
        </label>
        <label className="block">
          <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-1.5 block">To</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="ss-summary-to" className="bg-[#0a0a0d] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-[#ff5c00] rounded-sm" />
        </label>
        <button onClick={exportCsv} data-testid="ss-summary-export" className="ml-auto inline-flex items-center gap-2 border border-white/10 hover:border-[#ff5c00] hover:text-[#ff5c00] text-zinc-300 font-mono-accent text-xs uppercase tracking-widest px-4 py-2 rounded-sm">
          Export CSV
        </button>
      </div>

      <div className="bg-[#0f0f14] border border-white/10 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#080808] text-zinc-500 font-mono-accent text-[10px] uppercase tracking-widest">
            <tr>
              <th className="text-left p-3">Student</th>
              <th className="text-left p-3">Class</th>
              <th className="text-left p-3">Phone</th>
              <th className="text-right p-3">Sundays Present</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.student_id} data-testid={`ss-summary-row-${i}`} className="border-t border-white/5">
                <td className="p-3 text-white">{r.student_name}</td>
                <td className="p-3 text-zinc-300">{r.class_name || "—"}</td>
                <td className="p-3 text-zinc-400 font-mono-accent text-xs">{r.student_phone}</td>
                <td className="p-3 text-right font-display text-xl text-[#ff5c00]">{r.attendance_count}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-zinc-500">No attendance in this range.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MinistriesManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/ministries").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this ministry?")) return;
    await api.delete(`/admin/ministries/${id}`);
    load();
  };

  const move = async (index, dir) => {
    const next = index + dir;
    if (next < 0 || next >= items.length) return;
    const a = items[index];
    const b = items[next];
    // Swap orders and persist (uses full PUT)
    const aOrder = a.order ?? index;
    const bOrder = b.order ?? next;
    try {
      // Send full ministry payloads with swapped orders
      const stripIds = (m, newOrder) => {
        const { id, created_at, ...rest } = m;
        return { ...rest, order: newOrder };
      };
      await api.put(`/admin/ministries/${a.id}`, stripIds(a, bOrder));
      await api.put(`/admin/ministries/${b.id}`, stripIds(b, aOrder));
      load();
    } catch (e) {
      window.alert("Could not reorder. Try again.");
    }
  };

  const blankMinistry = () => ({
    id: null, slug: "", name: "", summary: "", icon: "Flame",
    hero_image: "", mission: "", leader_name: "", leader_title: "", leader_image: "",
    meeting_time: "", contact_email: "ugyouthchurch@gmail.com",
    sections: [], gallery_images: [],
    cta_text: "Join This Team", order: items.length + 1, is_active: true,
  });

  return (
    <Section
      title="Ministries"
      actions={
        <button
          onClick={() => setEditing(blankMinistry())}
          data-testid="ministry-add"
          className="inline-flex items-center gap-2 bg-[#ff5c00] hover:bg-[#ff7a33] text-white font-mono-accent text-xs uppercase tracking-widest px-4 py-2 rounded-sm"
        >
          <Plus className="w-3.5 h-3.5" /> New Ministry
        </button>
      }
    >
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((m, i) => (
          <div key={m.id} data-testid={`ministry-admin-row-${i}`} className="bg-[#0f0f14] border border-white/10 rounded-sm overflow-hidden">
            {m.hero_image && <img src={m.hero_image} alt="" className="w-full h-32 object-cover opacity-70" style={{ objectPosition: m.hero_image_position || "center", transform: `scale(${m.hero_image_scale || 1})`, transformOrigin: m.hero_image_position || "center" }} />}
            <div className="p-4">
              <p className="font-display text-lg uppercase tracking-tight">{m.name}</p>
              <p className="text-[10px] font-mono-accent uppercase tracking-widest text-zinc-500">/{m.slug} · order {m.order}</p>
              <p className="text-sm text-zinc-400 mt-2 line-clamp-2">{m.summary}</p>
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/5">
                <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`ministry-up-${i}`} className="text-zinc-400 hover:text-[#ff5c00] disabled:opacity-30 p-1.5" aria-label="Move up"><ArrowUp className="w-4 h-4" /></button>
                <button onClick={() => move(i, 1)} disabled={i === items.length - 1} data-testid={`ministry-down-${i}`} className="text-zinc-400 hover:text-[#ff5c00] disabled:opacity-30 p-1.5" aria-label="Move down"><ArrowDown className="w-4 h-4" /></button>
                <button onClick={() => setEditing(m)} data-testid={`ministry-edit-${i}`} className="text-[#ff5c00] hover:text-white p-1.5"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(m.id)} data-testid={`ministry-delete-${i}`} className="text-zinc-500 hover:text-red-400 p-1.5"><Trash2 className="w-4 h-4" /></button>
                <a href={`/ministries/${m.slug}`} target="_blank" rel="noreferrer" className="ml-auto font-mono-accent text-[10px] uppercase tracking-widest text-zinc-400 hover:text-[#ff5c00]">View →</a>
              </div>
            </div>
          </div>
        ))}
      </div>
      <MinistryEditDialog
        ministry={editing}
        open={!!editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    </Section>
  );
}

function MinistryEditDialog({ ministry, open, onClose, onSaved }) {
  const [form, setForm] = useState(ministry || {});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (ministry) { setForm(ministry); setError(""); } }, [ministry]);
  if (!ministry) return null;

  const setField = (k, v) => setForm({ ...form, [k]: v });
  const setSection = (i, patch) => {
    const list = [...(form.sections || [])];
    list[i] = { ...list[i], ...patch };
    setForm({ ...form, sections: list });
  };
  const addSection = () => setForm({ ...form, sections: [...(form.sections || []), { heading: "", body: "", image_url: "" }] });
  const removeSection = (i) => setForm({ ...form, sections: form.sections.filter((_, idx) => idx !== i) });
  const addGalleryImage = (url) => setForm({ ...form, gallery_images: [...(form.gallery_images || []), url] });
  const removeGalleryImage = (i) => setForm({ ...form, gallery_images: form.gallery_images.filter((_, idx) => idx !== i) });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = { ...form };
      delete payload.id; delete payload.created_at;
      if (ministry.id) {
        await api.put(`/admin/ministries/${ministry.id}`, payload);
      } else {
        await api.post("/admin/ministries", payload);
      }
      onSaved();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ministry-edit-dialog" className="bg-[#0a0a0d] border border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase tracking-tight">{ministry.id ? "Edit Ministry" : "New Ministry"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <Input label="Name" required value={form.name || ""} onChange={(e) => setField("name", e.target.value)} data-testid="ministry-name" />
            <Input label="Slug (URL)" required value={form.slug || ""} onChange={(e) => setField("slug", e.target.value.toLowerCase().replace(/\s+/g, "-"))} data-testid="ministry-slug" placeholder="e.g. worship-choir" />
            <Input label="Icon (Lucide name)" value={form.icon || "Flame"} onChange={(e) => setField("icon", e.target.value)} data-testid="ministry-icon" placeholder="Flame, Music, Users…" />
            <Input label="Order (sort)" type="number" value={form.order ?? 0} onChange={(e) => setField("order", Number(e.target.value))} data-testid="ministry-order" />
            <Input label="Meeting Time" value={form.meeting_time || ""} onChange={(e) => setField("meeting_time", e.target.value)} data-testid="ministry-meeting" />
            <Input label="Contact Email" value={form.contact_email || ""} onChange={(e) => setField("contact_email", e.target.value)} data-testid="ministry-email" />
          </div>
          <Textarea label="Summary" required value={form.summary || ""} onChange={(e) => setField("summary", e.target.value)} data-testid="ministry-summary" />
          <Textarea label="Mission" rows={3} value={form.mission || ""} onChange={(e) => setField("mission", e.target.value)} data-testid="ministry-mission" />

          <ImageField
            label="Hero Image"
            value={form.hero_image || ""}
            onChange={(v) => setField("hero_image", v)}
            position={form.hero_image_position || "center"}
            onPositionChange={(p) => setField("hero_image_position", p)}
            scale={form.hero_image_scale || 1}
            onScaleChange={(s) => setField("hero_image_scale", s)}
            testId="ministry-hero"
          />

          <div className="grid sm:grid-cols-2 gap-4">
            <Input label="Leader Name" value={form.leader_name || ""} onChange={(e) => setField("leader_name", e.target.value)} data-testid="ministry-leader-name" />
            <Input label="Leader Title" value={form.leader_title || ""} onChange={(e) => setField("leader_title", e.target.value)} data-testid="ministry-leader-title" />
          </div>
          <ImageField
            label="Leader Image"
            value={form.leader_image || ""}
            onChange={(v) => setField("leader_image", v)}
            position={form.leader_image_position || "center"}
            onPositionChange={(p) => setField("leader_image_position", p)}
            scale={form.leader_image_scale || 1}
            onScaleChange={(s) => setField("leader_image_scale", s)}
            testId="ministry-leader-img"
          />

          {/* Sections */}
          <div className="border-t border-white/10 pt-5 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-display text-lg uppercase tracking-tight">Sections</h4>
              <button type="button" onClick={addSection} data-testid="ministry-add-section" className="inline-flex items-center gap-1 text-[#ff5c00] hover:text-white font-mono-accent text-[10px] uppercase tracking-widest">
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
            {(form.sections || []).map((s, i) => (
              <div key={i} data-testid={`ministry-section-${i}`} className="bg-[#0f0f14] border border-white/10 rounded-sm p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="font-mono-accent text-[10px] uppercase tracking-widest text-zinc-500">Section #{i + 1}</span>
                  <button type="button" onClick={() => removeSection(i)} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
                <Input label="Heading" value={s.heading || ""} onChange={(e) => setSection(i, { heading: e.target.value })} data-testid={`ministry-section-heading-${i}`} />
                <Textarea label="Body" value={s.body || ""} onChange={(e) => setSection(i, { body: e.target.value })} data-testid={`ministry-section-body-${i}`} />
                <ImageField
                  label="Section Image (optional)"
                  value={s.image_url || ""}
                  onChange={(v) => setSection(i, { image_url: v })}
                  position={s.image_position || "center"}
                  onPositionChange={(p) => setSection(i, { image_position: p })}
                  scale={s.image_scale || 1}
                  onScaleChange={(sc) => setSection(i, { image_scale: sc })}
                  testId={`ministry-section-img-${i}`}
                />
              </div>
            ))}
          </div>

          {/* Gallery */}
          <div className="border-t border-white/10 pt-5 space-y-3">
            <h4 className="font-display text-lg uppercase tracking-tight">Gallery Images</h4>
            <ImageField label="Add an image (URL or upload)" value="" onChange={(v) => v && addGalleryImage(v)} testId="ministry-gallery-adder" />
            {(form.gallery_images || []).length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {form.gallery_images.map((src, i) => (
                  <div key={i} className="relative">
                    <img src={src} alt="" className="w-full h-20 object-cover rounded-sm" />
                    <button type="button" onClick={() => removeGalleryImage(i)} className="absolute top-1 right-1 bg-black/60 hover:bg-red-600 text-white w-6 h-6 rounded-sm flex items-center justify-center">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-4 border-t border-white/10 pt-5">
            <Input label="CTA Button Text" value={form.cta_text || ""} onChange={(e) => setField("cta_text", e.target.value)} data-testid="ministry-cta" />
            <label className="flex items-center gap-2 text-sm mt-7">
              <input type="checkbox" checked={!!form.is_active} onChange={(e) => setField("is_active", e.target.checked)} data-testid="ministry-active" className="accent-[#ff5c00]" />
              Active (visible on site)
            </label>
          </div>

          {error && <p className="text-red-400 text-sm">{error}</p>}
          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={saving} data-testid="ministry-save" className="inline-flex items-center gap-2 bg-[#ff5c00] hover:bg-[#ff7a33] disabled:opacity-60 text-white font-mono-accent text-xs uppercase tracking-widest px-5 py-2.5 rounded-sm">
              <CheckIcon className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save Ministry"}
            </button>
            <button type="button" onClick={onClose} className="font-mono-accent text-xs uppercase tracking-widest px-4 py-2.5 rounded-sm border border-white/10 hover:border-white/30 text-zinc-300">
              Cancel
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function GalleryManager() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filterCat, setFilterCat] = useState("All");
  const [editing, setEditing] = useState(null); // image object being edited
  const [mode, setMode] = useState("file");
  const [form, setForm] = useState({
    title: "", description: "", category: "Sunday Service",
    image_url: "", event_date: "", file: null,
  });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);

  const load = () => api.get("/gallery").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/gallery/categories").then((r) => setCategories(r.data));
  }, []);

  const handleFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError("Image too large (max 8 MB).");
      return;
    }
    setError("");
    setForm({ ...form, file });
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const reset = () => {
    setForm({ title: "", description: "", category: form.category, image_url: "", event_date: "", file: null });
    setPreview(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setUploading(true);
    try {
      if (mode === "file") {
        if (!form.file) { setError("Choose an image to upload."); setUploading(false); return; }
        const fd = new FormData();
        fd.append("file", form.file);
        if (form.title) fd.append("title", form.title);
        if (form.description) fd.append("description", form.description);
        fd.append("category", form.category);
        if (form.event_date) fd.append("event_date", form.event_date);
        await api.post("/admin/gallery/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        if (!form.image_url) { setError("Paste an image URL."); setUploading(false); return; }
        await api.post("/admin/gallery", {
          title: form.title || null,
          description: form.description || null,
          category: form.category,
          image_url: form.image_url,
          event_date: form.event_date || null,
        });
      }
      reset();
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this image?")) return;
    await api.delete(`/admin/gallery/${id}`);
    load();
  };

  const filtered = filterCat === "All" ? items : items.filter((i) => i.category === filterCat);

  return (
    <Section title="Gallery">
      <form onSubmit={submit} data-testid="gallery-form" className="bg-[#0f0f14] border border-white/10 rounded-sm p-6 mb-8 space-y-5">
        <div className="flex flex-wrap gap-2">
          {[
            { id: "file", label: "Upload File", icon: Upload },
            { id: "url", label: "Paste URL", icon: LinkIcon },
          ].map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setMode(m.id)}
              data-testid={`gallery-mode-${m.id}`}
              className={`inline-flex items-center gap-2 font-mono-accent text-[11px] tracking-[0.2em] uppercase px-3 py-2 rounded-sm border ${
                mode === m.id
                  ? "border-[#ff5c00] text-white bg-[#ff5c00]/10"
                  : "border-white/10 text-zinc-400 hover:text-white"
              }`}
            >
              <m.icon className="w-3.5 h-3.5" /> {m.label}
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2 space-y-4">
            {mode === "file" ? (
              <label className="block">
                <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2 block">Image File (≤ 8 MB)</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  data-testid="gallery-file-input"
                  className="block w-full text-sm text-zinc-400 file:mr-3 file:py-2 file:px-4 file:rounded-sm file:border-0 file:font-mono-accent file:text-[11px] file:tracking-widest file:uppercase file:bg-[#ff5c00] file:text-white hover:file:bg-[#ff7a33]"
                />
              </label>
            ) : (
              <Input
                label="Image URL"
                required
                value={form.image_url}
                onChange={(e) => { setForm({ ...form, image_url: e.target.value }); setPreview(e.target.value); }}
                data-testid="gallery-image-url"
                placeholder="https://…/photo.jpg"
              />
            )}

            <div className="grid sm:grid-cols-2 gap-4">
              <Input
                label="Title (optional)"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="gallery-title"
              />
              <Input
                label="Event Date (optional)"
                type="date"
                value={form.event_date}
                onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                data-testid="gallery-event-date"
              />
              <div>
                <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2 block">Category</span>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  data-testid="gallery-category"
                  className="w-full bg-[#0a0a0d] border border-white/10 px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#ff5c00] rounded-sm"
                >
                  {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <Textarea
              label="Description (optional)"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="gallery-description"
            />
          </div>

          <div className="border border-white/10 rounded-sm bg-[#0a0a0d] aspect-square flex items-center justify-center overflow-hidden">
            {preview ? (
              <img src={preview} alt="preview" data-testid="gallery-preview" className="w-full h-full object-cover" />
            ) : (
              <div className="text-center text-zinc-600 text-xs font-mono-accent uppercase tracking-widest p-6">
                <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                Preview
              </div>
            )}
          </div>
        </div>

        {error && <p data-testid="gallery-error" className="text-red-400 text-sm">{error}</p>}

        <button
          type="submit"
          disabled={uploading}
          data-testid="gallery-submit"
          className="inline-flex items-center gap-2 bg-[#ff5c00] hover:bg-[#ff7a33] disabled:opacity-60 text-white font-mono-accent text-xs uppercase tracking-widest px-5 py-3 rounded-sm"
        >
          <Plus className="w-4 h-4" /> {uploading ? "Uploading…" : "Add to Gallery"}
        </button>
      </form>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="font-mono-accent text-[10px] tracking-widest uppercase text-zinc-500">Filter:</span>
        {["All", ...categories].map((c) => (
          <button
            key={c}
            onClick={() => setFilterCat(c)}
            data-testid={`gallery-admin-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
            className={`font-mono-accent text-[11px] tracking-widest uppercase px-2.5 py-1 rounded-sm border ${
              filterCat === c
                ? "border-[#ff5c00] text-white bg-[#ff5c00]/10"
                : "border-white/10 text-zinc-400 hover:text-white"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p className="text-zinc-500 text-sm py-8 text-center">No images yet.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {filtered.map((img, i) => (
            <div
              key={img.id}
              data-testid={`gallery-admin-row-${i}`}
              className="group relative bg-[#0f0f14] border border-white/10 rounded-sm overflow-hidden hover:border-[#ff5c00]/40"
            >
              <div className="aspect-square overflow-hidden bg-[#050505]">
                <img src={img.image_url} alt={img.title || ""} className="w-full h-full object-cover" />
              </div>
              <div className="p-2.5 text-xs">
                {img.title && <p className="text-white truncate">{img.title}</p>}
                <p className="font-mono-accent text-[10px] tracking-widest uppercase text-[#ff5c00] mt-0.5">{img.category}</p>
                {img.event_date && <p className="font-mono-accent text-[10px] tracking-widest uppercase text-zinc-500">{img.event_date}</p>}
              </div>
              <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => setEditing(img)}
                  data-testid={`gallery-admin-edit-${i}`}
                  className="bg-black/60 hover:bg-[#ff5c00] text-white w-7 h-7 rounded-sm flex items-center justify-center"
                  aria-label="Edit"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => remove(img.id)}
                  data-testid={`gallery-admin-delete-${i}`}
                  className="bg-black/60 hover:bg-red-600 text-white w-7 h-7 rounded-sm flex items-center justify-center"
                  aria-label="Delete"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <GalleryEditDialog
        open={!!editing}
        image={editing}
        categories={categories}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    </Section>
  );
}

function GalleryEditDialog({ open, image, categories, onClose, onSaved }) {
  const [form, setForm] = useState({ title: "", description: "", category: "Other", event_date: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (image) {
      setForm({
        title: image.title || "",
        description: image.description || "",
        category: image.category || "Other",
        event_date: image.event_date || "",
      });
      setError("");
    }
  }, [image]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api.put(`/admin/gallery/${image.id}`, form);
      onSaved();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="gallery-edit-dialog" className="bg-[#0a0a0d] border border-white/10 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase tracking-tight">Edit Image</DialogTitle>
        </DialogHeader>
        {image && (
          <form onSubmit={save} className="space-y-4">
            <img src={image.image_url} alt="" className="w-full h-44 object-cover rounded-sm" />
            <Input label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="gallery-edit-title" />
            <div>
              <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2 block">Category</span>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="gallery-edit-category"
                className="w-full bg-[#050505] border border-white/10 px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#ff5c00] rounded-sm"
              >
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <Input label="Event Date" type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} data-testid="gallery-edit-date" />
            <Textarea label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="gallery-edit-description" />
            {error && <p className="text-red-400 text-sm">{error}</p>}
            <div className="flex gap-2 pt-2">
              <button type="submit" disabled={saving} data-testid="gallery-edit-save" className="inline-flex items-center gap-2 bg-[#ff5c00] hover:bg-[#ff7a33] disabled:opacity-60 text-white font-mono-accent text-xs uppercase tracking-widest px-5 py-2.5 rounded-sm">
                <CheckIcon className="w-3.5 h-3.5" /> Save
              </button>
              <button type="button" onClick={onClose} data-testid="gallery-edit-cancel" className="font-mono-accent text-xs uppercase tracking-widest px-4 py-2.5 rounded-sm border border-white/10 hover:border-white/30 text-zinc-300">
                Cancel
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ContactList() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/contact-messages").then((r) => setItems(r.data)); }, []);
  return (
    <Section title="Contact Messages">
      <div className="space-y-3">
        {items.length === 0 && <p className="text-zinc-500">No messages yet.</p>}
        {items.map((m, i) => (
          <div key={m.id} data-testid={`message-row-${i}`} className="bg-[#0f0f14] border border-white/10 rounded-sm p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="font-display uppercase tracking-tight">{m.name} <span className="text-zinc-500 text-sm normal-case">· {m.email}</span></p>
              <p className="text-[10px] font-mono-accent uppercase tracking-widest text-zinc-500">{m.created_at?.slice(0, 10)}</p>
            </div>
            <p className="text-xs text-[#ff5c00] uppercase tracking-widest font-mono-accent mb-2">{m.subject}</p>
            <p className="text-zinc-300 text-sm whitespace-pre-wrap">{m.message}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}
